import { FormEvent, useState } from 'react'
import { Trans } from 'react-i18next'
import { useSelector } from 'react-redux'

import { t } from 'i18next'
import lockImage from '~/assets/images/anatomy/lock.png'
import checkIcon from '~/assets/images/sprite/check.svg'
import SectionProgress from '~/pages/anatomyV2/libs/components/SectionProgress'
import AutoComplete from '~/pages/email/lib/components/AutoComplete'
import { useEmail } from '~/pages/email/lib/hooks/useEmail'
import { Input } from '~/shared/components/Input'
import { Modal } from '~/shared/components/Modal'
import { Spinner } from '~/shared/components/Spinner'
import { SvgImage } from '~/shared/components/SvgImage'
import { Cohort } from '~/shared/constants/commonConstants'
import { selectCohortID } from '~/shared/store/common/selects'
import { TPageProps } from '~/shared/types/commonTypes'

import { Styles as S } from './styles'

const SECTION_NUMBER = 6

const EmailAnatomy = ({ pageId }: TPageProps) => {
  const cohort = useSelector(selectCohortID)
  const isAnatomyV2 = cohort === Cohort.ANATOMY_V2
  const [isNewsletterChecked, setIsNewsletterChecked] = useState(true)

  const {
    areEmailTipsVisible,
    isErrorModalShown,
    isButtonDisabled,
    isStatusFetching,
    inputValue,
    isValidInputValue,
    filteredEmailDomains,
    handleSubmit,
    handleChange,
    handleCloseErrorModal,
    handlePrefilledEmail,
  } = useEmail()

  if (isStatusFetching) return <Spinner />

  return (
    <>
      <S.Root>
        <S.StyledContainer>
          {isAnatomyV2 && (
            <SectionProgress currentSection={SECTION_NUMBER} pageId={pageId} />
          )}

          <S.StyledForm
            onSubmit={(e: FormEvent<HTMLFormElement>) => handleSubmit(e)}
          >
            <S.Title $isAnatomyV2={isAnatomyV2}>
              <Trans i18nKey="email.title" />
            </S.Title>

            <S.Subtitle>
              <Trans i18nKey="email.subtitle" />
            </S.Subtitle>

            <S.InputWrapper>
              <Input
                value={inputValue}
                isValid={isValidInputValue}
                placeholder={t`email.placeholder`}
                validationText={t`email.error`}
                autoComplete="off"
                inputHeight={64}
                onChange={handleChange}
                required
              />

              {areEmailTipsVisible && !!filteredEmailDomains.length && (
                <AutoComplete
                  domains={filteredEmailDomains}
                  handlePrefilledEmail={handlePrefilledEmail}
                />
              )}
            </S.InputWrapper>

            <S.PrivacyRow>
              <S.LockIcon>
                <img src={lockImage} alt="lock" width={20} height={20} />
              </S.LockIcon>
              <S.PrivacyText>
                <Trans i18nKey="email.privacyText" />
              </S.PrivacyText>
            </S.PrivacyRow>

            <S.CheckboxRow onClick={() => setIsNewsletterChecked((v) => !v)}>
              <S.CheckboxContainer>
                <S.Checkbox
                  $checked={isNewsletterChecked}
                  $isAnatomyV2={isAnatomyV2}
                >
                  {isNewsletterChecked && (
                    <SvgImage svg={checkIcon} width={10} height={10} />
                  )}
                </S.Checkbox>
              </S.CheckboxContainer>
              <S.CheckboxLabel>
                <Trans i18nKey="email.newsletterConsent" />
              </S.CheckboxLabel>
            </S.CheckboxRow>

            <S.BonusCard>
              <S.BonusEmoji>🎁</S.BonusEmoji>
              <S.BonusContent>
                <S.BonusTitle>
                  <Trans i18nKey="email.bonusTitle" />
                </S.BonusTitle>
                <S.BonusSubtitle>
                  <Trans i18nKey="email.bonusSubtitle" />
                </S.BonusSubtitle>
              </S.BonusContent>
            </S.BonusCard>

            <S.StyledButton type="submit" disabled={isButtonDisabled}>
              <Trans i18nKey="actions.continue" />
            </S.StyledButton>
          </S.StyledForm>
        </S.StyledContainer>
      </S.Root>

      <Modal onClose={handleCloseErrorModal} isShown={isErrorModalShown}>
        {/* error message handled by parent Modal */}
      </Modal>
    </>
  )
}

export default EmailAnatomy
